import { Chip, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Stack } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { Children, HTMLAttributes, useState } from 'react';
import { FIFTH_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { joinStrings } from 'shared/utils/StringUtils';
import { ShipperDTO, useShippers } from '../../data/LoadboardShipperAPI';

function ListboxComponent({
  children,
  ...rest
}: HTMLAttributes<HTMLUListElement>) {
  const { length } = Children.toArray(children);
  return (
    <ul {...rest}>
      {children}
      {length === 5 && (
        <li className="MuiAutocomplete-option">
          <TextBox color="secondary">
            Showing only 5 results. Try searching with a full name for better
            results.
          </TextBox>
        </li>
      )}
    </ul>
  );
}

interface Props {
  type: 'include' | 'exclude';
  value: ShipperDTO[];
  error?: string | boolean;
  disabled?: boolean;
  onChange: (value: ShipperDTO[]) => void;
}

export function ShipperAutocomplete({
  type,
  disabled,
  value,
  error,
  onChange,
}: Props) {
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const searchQuery = useDebouncedValue(search, FIFTH_OF_A_SECOND);
  const { data = [], isFetching } = useShippers(searchQuery);

  return (
    <Autocomplete
      options={data}
      value={value}
      multiple={true}
      disabled={disabled}
      loading={isFetching}
      getOptionSelected={(option, selectedValue) =>
        option.guid === selectedValue.guid
      }
      filterSelectedOptions={true}
      filterOptions={(options) => options}
      onChange={(_event, newValue) => {
        onChange(newValue);
      }}
      renderTags={(tags, getTagProps) => {
        return tags.map((option, index) => (
          <Chip key={index} label={option.name} {...getTagProps({ index })} />
        ));
      }}
      ListboxComponent={ListboxComponent}
      renderOption={(option) => (
        <Stack space="none">
          <TextBox>{option.name}</TextBox>
          <TextBox color="secondary">
            {joinStrings(', ', option.city, option.state)}
          </TextBox>
        </Stack>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label=""
          error={!!error}
          placeholder={
            focused || value.length || type === 'exclude'
              ? 'Enter 3 characters to search'
              : 'All Shippers'
          }
          helperText={
            error || 'Only shippers with active postings are available.'
          }
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      )}
    />
  );
}
